import { h } from '@/lib/dom';
import type { UserPublic } from '@/api/types';
import { attemptLogout } from '@/lib/data';
import { clearSession } from '@/lib/session';
import { navigate, registerCleanup } from '@/router';
import { openDrawer } from './Drawer';

export interface NavbarProps {
  user: UserPublic;
  active?: string;
}

const LINKS = [
  { href: '/', label: 'Дашборд', key: 'home' },
  { href: '/labs', label: 'Анализы', key: 'labs' },
  { href: '/training', label: 'Тренировки', key: 'training' },
  { href: '/sleep', label: 'Сон', key: 'sleep' },
  { href: '/nutrition', label: 'Питание', key: 'nutrition' },
  { href: '/meds', label: 'Препараты', key: 'meds' },
  { href: '/measurements', label: 'Измерения', key: 'measurements' },
  { href: '/documents', label: 'Документы', key: 'documents' },
  { href: '/agenda', label: 'К обсуждению', key: 'agenda' },
];

function usernameInitials(username: string): string {
  return username.slice(0, 2).toUpperCase();
}

function buildLinks(active: string | undefined): HTMLElement[] {
  return LINKS.map((link) =>
    h('li', null,
      h('a', {
        class: 'navbar-link',
        href: link.href,
        aria: active === link.key ? { current: 'page' } : undefined,
      }, link.label),
    ),
  );
}

async function onLogout(): Promise<void> {
  await attemptLogout();
  clearSession();
  navigate('/login');
}

function UserMenu(user: UserPublic): HTMLElement {
  const menuItems: HTMLElement[] = [];
  if (user.role === 'owner') {
    menuItems.push(
      h('li', { role: 'none' },
        h('a', { href: '/settings', role: 'menuitem' }, 'Настройки')),
    );
  }
  menuItems.push(
    h('li', { role: 'none' },
      h('button', {
        type: 'button',
        role: 'menuitem',
        on: { click: () => void onLogout() },
      }, 'Выйти')),
  );

  const popup = h('ul', { class: 'user-menu-popup', role: 'menu' }, menuItems);
  const trigger = h('button', {
    class: 'user-menu-trigger',
    type: 'button',
    aria: { haspopup: 'menu', expanded: 'false' },
  }, [
    h('span', { class: 'avatar', 'aria-hidden': 'true' }, usernameInitials(user.username)),
    ' ',
    h('span', null, user.username),
    ' ',
    h('span', { 'aria-hidden': 'true' }, '▾'),
  ]);
  const menu = h('div', { class: 'user-menu' }, [trigger, popup]);

  function open(): void {
    menu.classList.add('open');
    trigger.setAttribute('aria-expanded', 'true');
  }
  function close(): void {
    menu.classList.remove('open');
    trigger.setAttribute('aria-expanded', 'false');
  }

  trigger.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (menu.classList.contains('open')) { close(); } else { open(); }
  });

  // Document-level listeners must be torn down on navigation — the shell (and
  // this menu) is rebuilt on every route, so anonymous handlers would otherwise
  // accumulate for the tab's lifetime. Router drains registered cleanups before
  // mounting the next view.
  function onDocClick(ev: MouseEvent): void {
    if (!menu.contains(ev.target as Node)) close();
  }
  function onDocKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') close();
  }
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onDocKeydown);
  registerCleanup(() => {
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onDocKeydown);
  });

  return menu;
}

export function Navbar(props: NavbarProps): HTMLElement {
  const hamburger = h('button', {
    class: 'navbar-hamburger',
    type: 'button',
    aria: { label: 'Открыть меню' },
    on: {
      click: () => {
        openDrawer({
          title: 'Меню',
          content: (close) =>
            h('div', null, LINKS.map((link) =>
              h('a', {
                class: 'drawer-nav-link',
                href: link.href,
                aria: props.active === link.key ? { current: 'page' } : undefined,
                on: { click: () => close() },
              }, link.label),
            )),
        });
      },
    },
  }, '☰');

  return h('header', { class: 'navbar', role: 'banner' }, [
    hamburger,
    h('div', { class: 'navbar-brand' }, [
      h('img', {
        src: '/favicon.svg',
        class: 'navbar-favicon',
        alt: '',
        'aria-hidden': 'true',
      }),
      'Health Dashboard',
    ]),
    h('nav', { 'aria-label': 'Основная навигация' },
      h('ul', { class: 'navbar-links' }, buildLinks(props.active))),
    h('div', { class: 'navbar-actions' }, UserMenu(props.user)),
  ]);
}
